import { useEffect, useState, useMemo } from 'react'
import { supabase } from '../services/supabase'
import AdminLayout from '../components/AdminLayout'

interface OrderItem {
  id: string
  product_id: string
  variant_id: string | null
  variant_name: string | null
  variant_value: string | null
  quantity: number
  price_at_purchase: number
  product?: {
    name: string
    image_urls: string[]
  }
}

interface ShippingAddress {
  full_name: string
  street_address: string
  city: string
  state: string
  postal_code: string
  country: string
  phone: string
  email?: string
  subtotal?: number
  shipping_cost?: number
  tax_amount?: number
  discount_amount?: number
}

interface AdminOrder {
  id: string
  customer_id: string | null
  guest_email: string | null
  guest_name: string | null
  is_guest_order: boolean
  total_amount: number
  discount_amount: number
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled'
  shipping_address: ShippingAddress
  payment_intent_id: string | null
  created_at: string
  updated_at: string
  items?: OrderItem[]
}

type StatusFilter = 'all' | 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled'

const statusColors: Record<string, { bg: string; text: string }> = {
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  processing: { bg: 'bg-blue-100', text: 'text-blue-800' },
  shipped: { bg: 'bg-purple-100', text: 'text-purple-800' },
  delivered: { bg: 'bg-green-100', text: 'text-green-800' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-800' },
}

const statusFilters: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
]

const statusOptions = ['pending', 'processing', 'shipped', 'delivered', 'cancelled']

export default function AdminOrders() {
  const [orders, setOrders] = useState<AdminOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedOrder, setSelectedOrder] = useState<AdminOrder | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [updatingOrderId, setUpdatingOrderId] = useState<string | null>(null)
  const [updateMessage, setUpdateMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Fetch all orders
  useEffect(() => {
    fetchOrders()
  }, [])

  const fetchOrders = async () => {
    setLoading(true)
    setError(null)

    try {
      // Fetch all orders (customer info is stored in shipping_address and guest fields)
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false })

      if (ordersError) throw ordersError

      if (!ordersData || ordersData.length === 0) {
        setOrders([])
        setLoading(false)
        return
      }

      // Fetch all order items with product info
      const orderIds = ordersData.map(o => o.id)
      const { data: itemsData, error: itemsError } = await supabase
        .from('order_items')
        .select(`
          id,
          order_id,
          product_id,
          variant_id,
          variant_name,
          variant_value,
          quantity,
          price_at_purchase,
          product:products(name, image_urls)
        `)
        .in('order_id', orderIds)

      if (itemsError) throw itemsError

      // Combine orders with their items
      const ordersWithItems = ordersData.map(order => ({
        ...order,
        items: (itemsData || [])
          .filter(item => item.order_id === order.id)
          .map(item => ({
            id: item.id,
            product_id: item.product_id,
            variant_id: item.variant_id,
            variant_name: item.variant_name,
            variant_value: item.variant_value,
            quantity: item.quantity,
            price_at_purchase: item.price_at_purchase,
            product: (Array.isArray(item.product) ? item.product[0] : item.product) as { name: string; image_urls: string[] } | undefined
          }))
      }))

      setOrders(ordersWithItems)
    } catch (err) {
      console.error('Error fetching orders:', err)
      setError('Failed to load orders. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Update order status
  const updateOrderStatus = async (orderId: string, newStatus: string) => {
    setUpdatingOrderId(orderId)
    setUpdateMessage(null)

    // Store previous status for rollback
    const previousOrders = [...orders]

    // Optimistic update
    setOrders(prev => prev.map(order =>
      order.id === orderId ? { ...order, status: newStatus as AdminOrder['status'] } : order
    ))

    // Also update selected order if it's open
    if (selectedOrder?.id === orderId) {
      setSelectedOrder(prev => prev ? { ...prev, status: newStatus as AdminOrder['status'] } : null)
    }

    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: newStatus })
        .eq('id', orderId)

      if (error) throw error

      setUpdateMessage({ type: 'success', text: 'Status updated successfully' })
      setTimeout(() => setUpdateMessage(null), 3000)
    } catch (err) {
      console.error('Error updating order status:', err)
      // Revert on error
      setOrders(previousOrders)
      if (selectedOrder?.id === orderId) {
        const originalOrder = previousOrders.find(o => o.id === orderId)
        if (originalOrder) setSelectedOrder(originalOrder)
      }
      setUpdateMessage({ type: 'error', text: 'Failed to update status' })
      setTimeout(() => setUpdateMessage(null), 3000)
    } finally {
      setUpdatingOrderId(null)
    }
  }

  // Filter orders
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      // Status filter
      if (statusFilter !== 'all' && order.status !== statusFilter) {
        return false
      }

      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim()

        // Check order ID
        const orderIdMatch = order.id.toLowerCase().includes(query)

        // Check customer name (from guest fields or shipping address)
        const customerName = order.guest_name || order.shipping_address?.full_name
        const nameMatch = customerName?.toLowerCase().includes(query)

        // Check customer email (from guest fields or shipping address)
        const customerEmail = order.guest_email || order.shipping_address?.email
        const emailMatch = customerEmail?.toLowerCase().includes(query)

        if (!orderIdMatch && !nameMatch && !emailMatch) {
          return false
        }
      }

      return true
    })
  }, [orders, searchQuery, statusFilter])

  // Helper functions
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatOrderId = (id: string) => {
    return id.substring(0, 8).toUpperCase()
  }

  const getCustomerName = (order: AdminOrder) => {
    // Use guest_name for guest orders, otherwise fall back to shipping_address
    return order.guest_name || order.shipping_address?.full_name || 'Unknown'
  }

  const getCustomerEmail = (order: AdminOrder) => {
    // Use guest_email for guest orders, otherwise fall back to shipping_address
    return order.guest_email || order.shipping_address?.email || '-'
  }

  const clearFilters = () => {
    setSearchQuery('')
    setStatusFilter('all')
  }

  const hasActiveFilters = searchQuery.trim() !== '' || statusFilter !== 'all'

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Order Management</h1>
          <p className="text-gray-500 text-sm">View and manage all customer orders</p>
        </div>

        {/* Update Message */}
        {updateMessage && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${
            updateMessage.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {updateMessage.text}
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-400">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search by order ID, customer name, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-400 hover:text-gray-600">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Refresh Button */}
            <button
              onClick={fetchOrders}
              disabled={loading}
              className="px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
            </button>
          </div>

          {/* Status Filter Buttons */}
          <div className="flex flex-wrap gap-2 mt-4">
            {statusFilters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setStatusFilter(filter.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === filter.value
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-500">
            {filteredOrders.length === orders.length
              ? `${orders.length} order${orders.length !== 1 ? 's' : ''}`
              : `${filteredOrders.length} of ${orders.length} orders`}
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-500 text-sm">Loading orders...</p>
          </div>
        ) : error ? (
          /* Error State */
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-red-600">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            </div>
            <p className="text-gray-700 mb-4">{error}</p>
            <button
              onClick={fetchOrders}
              className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : orders.length === 0 ? (
          /* Empty State */
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-gray-400">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">No orders yet</h2>
            <p className="text-gray-500">Orders will appear here once customers start purchasing.</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          /* No Results State */
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-gray-400">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">No orders found</h2>
            <p className="text-gray-500 mb-6">Try adjusting your search or filters.</p>
            <button
              onClick={clearFilters}
              className="px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          /* Orders Table */
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Order</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Total</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredOrders.map((order) => {
                    const statusStyle = statusColors[order.status] || statusColors.pending

                    return (
                      <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                        {/* Order ID */}
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-medium text-gray-900">
                              #{formatOrderId(order.id)}
                            </span>
                            {order.is_guest_order && (
                              <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-600">
                                Guest
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Customer */}
                        <td className="px-4 py-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{getCustomerName(order)}</p>
                            <p className="text-xs text-gray-500">{getCustomerEmail(order)}</p>
                          </div>
                        </td>

                        {/* Date */}
                        <td className="px-4 py-4">
                          <span className="text-sm text-gray-600">{formatDate(order.created_at)}</span>
                        </td>

                        {/* Total */}
                        <td className="px-4 py-4">
                          <span className="text-sm font-semibold text-gray-900">${order.total_amount.toFixed(2)}</span>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-4">
                          <select
                            value={order.status}
                            onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                            disabled={updatingOrderId === order.id}
                            className={`px-2 py-1 text-xs font-medium rounded-lg border-0 cursor-pointer focus:ring-2 focus:ring-purple-500 ${statusStyle.bg} ${statusStyle.text} ${
                              updatingOrderId === order.id ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          >
                            {statusOptions.map((status) => (
                              <option key={status} value={status}>
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-4">
                          <button
                            onClick={() => setSelectedOrder(order)}
                            className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Order Detail Modal */}
        {selectedOrder && (
          <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 99999 }}
            onClick={(e) => e.target === e.currentTarget && setSelectedOrder(null)}
          >
            <div
              className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="text-lg font-bold text-gray-900">
                    Order #{formatOrderId(selectedOrder.id)}
                  </h2>
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${statusColors[selectedOrder.status]?.bg} ${statusColors[selectedOrder.status]?.text}`}>
                    {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}
                  </span>
                  {selectedOrder.is_guest_order && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                      Guest Order
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Status Update */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Update Status</label>
                  <select
                    value={selectedOrder.status}
                    onChange={(e) => updateOrderStatus(selectedOrder.id, e.target.value)}
                    disabled={updatingOrderId === selectedOrder.id}
                    className={`w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                      updatingOrderId === selectedOrder.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                    }`}
                  >
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Customer Info */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Customer Information</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Name</span>
                      <span className="text-sm font-medium text-gray-900">{getCustomerName(selectedOrder)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Email</span>
                      <span className="text-sm font-medium text-gray-900">{getCustomerEmail(selectedOrder)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Customer Type</span>
                      <span className="text-sm font-medium text-gray-900">
                        {selectedOrder.is_guest_order ? 'Guest' : 'Registered'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Shipping Address</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="font-medium text-gray-900">{selectedOrder.shipping_address?.full_name}</p>
                    <p className="text-sm text-gray-600 mt-1">{selectedOrder.shipping_address?.street_address}</p>
                    <p className="text-sm text-gray-600">
                      {selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.state} {selectedOrder.shipping_address?.postal_code}
                    </p>
                    <p className="text-sm text-gray-600">{selectedOrder.shipping_address?.country}</p>
                    {selectedOrder.shipping_address?.phone && (
                      <p className="text-sm text-gray-500 mt-2">Phone: {selectedOrder.shipping_address.phone}</p>
                    )}
                  </div>
                </div>

                {/* Order Items */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Order Items</h3>
                  <div className="space-y-3">
                    {selectedOrder.items?.map((item) => (
                      <div key={item.id} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                        {/* Product Image */}
                        <div className="w-12 h-12 bg-white rounded-lg overflow-hidden flex-shrink-0 border border-gray-100">
                          {item.product?.image_urls?.[0] ? (
                            <img
                              src={item.product.image_urls[0]}
                              alt={item.product.name}
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="w-5 h-5 text-gray-300">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                              </svg>
                            </div>
                          )}
                        </div>

                        {/* Product Details */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {item.product?.name || 'Unknown Product'}
                          </p>
                          {item.variant_name && item.variant_value && (
                            <p className="text-xs text-purple-600">
                              {item.variant_name}: {item.variant_value}
                            </p>
                          )}
                          <p className="text-xs text-gray-500">
                            Qty: {item.quantity} x ${item.price_at_purchase.toFixed(2)}
                          </p>
                        </div>

                        {/* Item Total */}
                        <span className="text-sm font-medium text-gray-900">
                          ${(item.quantity * item.price_at_purchase).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Order Totals */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Order Summary</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Subtotal</span>
                      <span className="text-gray-900">${(selectedOrder.shipping_address?.subtotal || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Shipping</span>
                      <span className="text-gray-900">
                        {(selectedOrder.shipping_address?.shipping_cost || 0) === 0
                          ? 'FREE'
                          : `$${(selectedOrder.shipping_address?.shipping_cost || 0).toFixed(2)}`}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Tax</span>
                      <span className="text-gray-900">${(selectedOrder.shipping_address?.tax_amount || 0).toFixed(2)}</span>
                    </div>
                    {((selectedOrder.discount_amount || 0) + (selectedOrder.shipping_address?.discount_amount || 0)) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Discount</span>
                        <span className="text-green-600">-${(selectedOrder.discount_amount || selectedOrder.shipping_address?.discount_amount || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-semibold pt-2 border-t border-gray-200">
                      <span className="text-gray-900">Total</span>
                      <span className="text-gray-900">${selectedOrder.total_amount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment & Order Info */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Order Details</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Order ID</span>
                      <span className="text-gray-900 font-mono text-xs">{selectedOrder.id}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Placed on</span>
                      <span className="text-gray-900">{formatDateTime(selectedOrder.created_at)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Last updated</span>
                      <span className="text-gray-900">{formatDateTime(selectedOrder.updated_at)}</span>
                    </div>
                    {selectedOrder.payment_intent_id && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Payment ID</span>
                        <span className="text-gray-900 font-mono text-xs">{selectedOrder.payment_intent_id}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
